import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FadeIn from '@/components/FadeIn';
import UnsolicitedPolicyModal from '@/components/UnsolicitedPolicyModal';
import { Info, Phone, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const LOGO_AVIF = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/jfotexpw_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.avif';
const LOGO_WEBP = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/76ox2huh_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.webp';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactPage() {
  const { language, t } = useLanguage();
  const c = t.contactPage;
  const { scrollYProgress } = useScroll();
  const ringRotation = useTransform(scrollYProgress, [0, 1], [0, 360]);

  // Form state
  const [topic, setTopic] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [comments, setComments] = useState('');
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  // Modals
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [finalErrorModalOpen, setFinalErrorModalOpen] = useState(false);
  const [errorPolicyModalOpen, setErrorPolicyModalOpen] = useState(false);
  const [lastSentTopic, setLastSentTopic] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = c.nameError;
    if (!email.trim() || !isValidEmail(email)) errs.email = c.emailError;
    if (!subject.trim()) errs.subject = c.subjectError;
    if (!comments.trim()) errs.comments = c.commentsError;
    if (!topic) errs.topic = true;
    return errs;
  };

  const resetForm = () => {
    setTopic(''); setName(''); setEmail(''); setSubject(''); setComments('');
    setPolicyAgreed(false); setErrors({}); setTouched(false);
  };

  const getTopicLabel = (key) => {
    const labels = { support: c.topicSupport, feedback: c.topicFeedback, data: c.topicData, privacy: c.topicPrivacy };
    return labels[key] || key;
  };

  const handleSubmit = async () => {
    setTouched(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0 || !policyAgreed) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, name, email, subject, comments }),
      });
      if (!res.ok) throw new Error('Failed');
      setLastSentTopic(topic);
      setSuccessModalOpen(true);
      resetForm();
      setAttemptCount(0);
    } catch {
      const newCount = attemptCount + 1;
      setAttemptCount(newCount);
      if (newCount >= 3) {
        setFinalErrorModalOpen(true);
      } else {
        setErrorModalOpen(true);
      }
    } finally {
      setSending(false);
    }
  };

  const handleErrorEmailClick = () => {
    setFinalErrorModalOpen(false);
    setErrorPolicyModalOpen(true);
  };

  const handleErrorPolicyAgree = () => {
    setErrorPolicyModalOpen(false);
    const mailSubject = encodeURIComponent(`[Contato] ${subject || 'Formulário de contato'}`);
    const mailBody = encodeURIComponent(`Nome: ${name}\nE-mail: ${email}\nAssunto: ${subject}\n\nComentários:\n${comments}`);
    window.location.href = `mailto:form@wee-time.com?subject=${mailSubject}&body=${mailBody}`;
  };

  const handlePolicyAgree = () => {
    setPolicyAgreed(true);
    setPolicyModalOpen(false);
  };

  const successMessage = lastSentTopic === 'feedback'
    ? c.successFeedback
    : c.successDefault.replace('{topic}', getTopicLabel(lastSentTopic));

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-lg bg-transparent border text-sm font-light text-[#333333] placeholder:text-[#333333]/40 focus:outline-none focus:ring-1 transition-colors duration-300 ${
      touched && errors[field]
        ? 'border-red-400/60 bg-red-50/30 focus:ring-red-400/30'
        : 'border-[#333333]/15 focus:ring-[#35589B]/30 focus:border-[#35589B]/40'
    }`;

  return (
    <div className="bg-[#F9F8F6] min-h-screen" data-testid="contact-page">
      <Header />

      {/* Right pane - Fixed logo (desktop) */}
      <div className="hidden md:flex fixed right-0 top-0 w-1/2 h-screen items-center justify-center bg-[#F9F8F6] z-10">
        <svg className="absolute w-[600px] h-[600px] opacity-[0.03]" viewBox="0 0 600 600">
          <ellipse cx="300" cy="300" rx="280" ry="260" fill="#C2A8F7" />
        </svg>
        <div className="relative w-[85%] max-w-[700px] aspect-square">
          <picture>
            <source srcSet={LOGO_AVIF} type="image/avif" />
            <source srcSet={LOGO_WEBP} type="image/webp" />
            <img src={LOGO_WEBP} alt={t.slogan.logoAlt} loading="lazy" decoding="async" width={1200} height={1200} className="w-full h-full object-contain relative z-10" />
          </picture>
          <motion.div className="absolute inset-[-8%] rounded-full border border-[#35589B]/[0.07] z-0" style={{ rotate: ringRotation }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#35589B]/20" />
          </motion.div>
        </div>
      </div>

      {/* Left pane - Content */}
      <main className="relative w-full md:w-1/2">
        <section className="min-h-screen flex flex-col justify-start px-8 md:px-16 lg:px-20 pt-28 pb-16">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-10">
            <div className="w-72 aspect-square">
              <picture>
                <source srcSet={LOGO_AVIF} type="image/avif" />
                <source srcSet={LOGO_WEBP} type="image/webp" />
                <img src={LOGO_WEBP} alt={t.slogan.logoAlt} loading="lazy" decoding="async" width={1200} height={1200} className="w-full h-full object-contain" />
              </picture>
            </div>
          </div>

          <FadeIn>
            <h1 data-testid="contact-title" className="text-3xl md:text-4xl lg:text-5xl font-light text-[#873D77] tracking-tight leading-tight mb-4">
              {c.title}
            </h1>
            <p className="text-base font-light text-[#333333]/90 leading-relaxed mb-10 max-w-lg">{c.subtitle}</p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="space-y-5 max-w-lg">
              {/* Topic dropdown */}
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger
                  data-testid="contact-topic-select"
                  className={`w-full py-3 bg-transparent text-sm font-light ${
                    touched && errors.topic ? 'border-red-400/60 bg-red-50/30' : 'border-[#333333]/15'
                  }`}
                >
                  <SelectValue placeholder={c.topicPlaceholder} />
                </SelectTrigger>
                <SelectContent className="bg-[#F9F8F6] border-[#333333]/10">
                  <SelectItem value="support" className="text-sm font-light">{c.topicSupport}</SelectItem>
                  <SelectItem value="feedback" className="text-sm font-light">{c.topicFeedback}</SelectItem>
                  <SelectItem value="data" className="text-sm font-light">{c.topicData}</SelectItem>
                  <SelectItem value="privacy" className="text-sm font-light">{c.topicPrivacy}</SelectItem>
                </SelectContent>
              </Select>

              {/* Required fields notice */}
              <p className="text-xs font-light text-[#333333]/50">{c.requiredFields}</p>

              {/* Name */}
              <div>
                <input data-testid="contact-name" type="text" placeholder={c.namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} className={inputClass('name')} />
                {touched && errors.name && <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500/80"><Info size={12} />{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <input data-testid="contact-email" type="email" placeholder={c.emailPlaceholder} value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass('email')} />
                {touched && errors.email && <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500/80"><Info size={12} />{errors.email}</p>}
              </div>

              {/* Subject */}
              <div>
                <input data-testid="contact-subject" type="text" placeholder={c.subjectPlaceholder} value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass('subject')} />
                {touched && errors.subject && <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500/80"><Info size={12} />{errors.subject}</p>}
              </div>

              {/* Comments */}
              <div>
                <textarea data-testid="contact-comments" placeholder={c.commentsPlaceholder} value={comments} onChange={(e) => setComments(e.target.value)} rows={5} className={`${inputClass('comments')} resize-none`} />
                {touched && errors.comments && <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500/80"><Info size={12} />{errors.comments}</p>}
              </div>

              {/* Policy checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="form-policy"
                  data-testid="contact-policy-checkbox"
                  checked={policyAgreed}
                  disabled
                  className="mt-0.5 border-[#333333]/30 data-[state=checked]:bg-[#35589B] data-[state=checked]:border-[#35589B]"
                />
                <label className="text-xs font-light text-[#333333]/70 leading-relaxed">
                  {c.policyPrefix}
                  <button
                    data-testid="contact-policy-link"
                    onClick={(e) => { e.preventDefault(); setPolicyModalOpen(true); }}
                    className="text-[#35589B] hover:underline"
                  >
                    {c.policyLinkText}
                  </button>
                  {c.policySuffix}
                </label>
              </div>

              {/* Submit button */}
              <button
                data-testid="contact-submit-btn"
                onClick={handleSubmit}
                disabled={sending || !policyAgreed}
                className={`flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-light tracking-wider transition-all duration-300 ${
                  policyAgreed
                    ? 'bg-[#35589B] text-white hover:bg-[#35589B]/90'
                    : 'bg-[#333333]/20 text-[#333333]/40 cursor-not-allowed'
                }`}
              >
                {sending ? c.submitting : c.submitButton}
                {!sending && <Send size={14} />}
              </button>
            </div>
          </FadeIn>

          {/* Phone banner */}
          <FadeIn delay={0.4}>
            <a
              href="tel:+5594984418762"
              data-testid="contact-phone-banner"
              className="flex items-center justify-center gap-3 mt-12 py-4 px-6 rounded-xl bg-[#C2A8F7]/10 border border-[#C2A8F7]/15 hover:bg-[#C2A8F7]/15 transition-colors duration-300 max-w-lg"
            >
              <Phone size={18} className="text-[#873D77]" />
              <span className="text-sm font-light text-[#333333]/90">{c.phoneBanner}</span>
            </a>
          </FadeIn>
        </section>

        <Footer />
      </main>

      {/* Unsolicited Policy Modal (from form) */}
      <UnsolicitedPolicyModal open={policyModalOpen} onClose={() => setPolicyModalOpen(false)} onAgree={handlePolicyAgree} />

      {/* Unsolicited Policy Modal (from error flow) */}
      <UnsolicitedPolicyModal open={errorPolicyModalOpen} onClose={() => setErrorPolicyModalOpen(false)} onAgree={handleErrorPolicyAgree} />

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={(v) => { if (!v) setSuccessModalOpen(false); }}>
        <DialogContent data-testid="contact-success-modal" className="bg-[#F9F8F6] border-[#333333]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-light text-[#47A2A2]">
              <CheckCircle size={20} /> {c.successTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">{successMessage}</p>
        </DialogContent>
      </Dialog>

      {/* Error Modal (retry) */}
      <Dialog open={errorModalOpen} onOpenChange={(v) => { if (!v) setErrorModalOpen(false); }}>
        <DialogContent data-testid="contact-error-modal" className="bg-[#F9F8F6] border-[#333333]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-light text-red-500/80">
              <AlertCircle size={20} /> {c.errorTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-light text-[#333333]/90 leading-relaxed">{c.errorRetry}</p>
        </DialogContent>
      </Dialog>

      {/* Final Error Modal (after 3 attempts) */}
      <Dialog open={finalErrorModalOpen} onOpenChange={(v) => { if (!v) setFinalErrorModalOpen(false); }}>
        <DialogContent data-testid="contact-final-error-modal" className="bg-[#F9F8F6] border-[#333333]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-light text-red-500/80">
              <AlertCircle size={20} /> {c.errorTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-light text-[#333333]/90 leading-relaxed">
            {c.errorFinal}{' '}
            <button
              data-testid="contact-error-email-link"
              onClick={handleErrorEmailClick}
              className="text-[#35589B] hover:underline"
            >
              form@wee-time.com
            </button>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
